// Вузол накопичувача (УЗЕ) на шині 10 кВ — поруч із КТП.
//
// Стоїть на тому самому рівні, що й КТП, бо приєднаний до тієї ж шини, але
// власних груп інверторів не має: під ним схема просто закінчується.
import i18n from '@/i18n';
import { scada, stateColor } from '@/scada/tokens';
import type { SchemeBess } from './types';

/** Батарейна шафа: корпус, секції модулів, індикатор заряду. */
function BessIcon({ color }: { color: string }) {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <rect x="5" y="6" width="16" height="15" rx="1.5"
        stroke={scada.textDim} strokeWidth="1.2" fill={scada.panel2} />
      {/* Клеми */}
      <line x1="9" y1="3.5" x2="9" y2="6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="17" y1="3.5" x2="17" y2="6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      {/* Модулі */}
      <line x1="7.5" y1="10" x2="18.5" y2="10" stroke={scada.border} strokeWidth="1" />
      <line x1="7.5" y1="13.5" x2="18.5" y2="13.5" stroke={scada.border} strokeWidth="1" />
      <line x1="7.5" y1="17" x2="18.5" y2="17" stroke={scada.border} strokeWidth="1" />
      {/* Індикатор стану — заповнена смуга знизу */}
      <rect x="7.5" y="17.8" width="11" height="2.4" rx="1" fill={`${color}55`} />
    </svg>
  );
}

export default function BessNode({ bess, selected, onSelect }: {
  bess: SchemeBess;
  selected: boolean;
  onSelect: () => void;
}) {
  const c = stateColor(bess.state);
  // Напрям потоку читається зі знаку: + віддає в мережу, − бере з неї.
  const flowing = bess.mode === 'charging' || bess.mode === 'discharging';

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer',
        background: scada.panel,
        border: `1px solid ${selected ? c : bess.state === 'ok' ? scada.border : `${c}55`}`,
        outline: selected ? `1px solid ${c}` : 'none',
        outlineOffset: 1,
        borderRadius: 10, padding: '10px 11px', display: 'grid', gap: 7,
        boxShadow: 'var(--shadow)', minWidth: 0,
        transition: 'border-color .15s ease',
      }}
    >
      <div className="flex items-center" style={{ gap: 7, minWidth: 0 }}>
        <BessIcon color={c} />
        <div style={{ minWidth: 0 }}>
          <div className="font-display" style={{
            fontSize: 13, fontWeight: 700, color: scada.text, lineHeight: 1.1,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{i18n.t('ak_scada2_bess_2')}</div>
          <div className="flex items-center" style={{ gap: 4, marginTop: 2 }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: c, flexShrink: 0 }} />
            <span className="font-display" style={{
              fontSize: 9, fontWeight: 600, letterSpacing: '.04em',
              textTransform: 'uppercase', color: c, whiteSpace: 'nowrap',
            }}>{bess.modeLabel}</span>
          </div>
        </div>
      </div>

      <div className="tabular-nums" style={{ fontSize: 10.5, color: scada.textMuted, whiteSpace: 'nowrap' }}>
        {i18n.t('ak_scada2_charge')} {bess.socPct}%
      </div>

      <div className="font-display tabular-nums" style={{
        fontSize: 19, fontWeight: 700, color: scada.text, lineHeight: 1,
      }}>
        {flowing ? Math.abs(Math.round(bess.powerKw)) : 0}
        <span style={{ fontSize: 10, fontWeight: 500, color: scada.textMuted }}> {i18n.t('ak_scada2_kw')}</span>
      </div>

      {/* Шкала заряду — єдиний показник УЗЕ, який читається миттєво. */}
      <div style={{ borderTop: `1px solid ${scada.border}`, paddingTop: 6, display: 'grid', gap: 5 }}>
        <div style={{ height: 5, borderRadius: 3, background: scada.border, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.max(0, Math.min(100, bess.socPct))}%`,
            height: '100%', background: c, borderRadius: 3,
          }} />
        </div>
        <div className="flex items-baseline justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 10, color: scada.textMuted, whiteSpace: 'nowrap' }}>{i18n.t('ak_scada2_health')}</span>
          <span className="tabular-nums" style={{ fontSize: 11, color: scada.textDim, whiteSpace: 'nowrap' }}>
            {bess.sohPct != null ? `${bess.sohPct}%` : '—'}
          </span>
        </div>
      </div>
    </button>
  );
}
